import { Box, Flex, Input, Text, Textarea, Spinner } from '@chakra-ui/react';
import { useCallback, useContext, useEffect, useState } from 'react';
import { GlobalContext } from 'providers/Global';
import { TokenAssociationData } from '../types';
import { isValidAddress } from 'utils/general';
import { getMirrorBaseUrl } from 'utils/mirrorUrl';
import { getAuthAxiosInstance as authAxios } from 'api/axios';

interface TokenAssociationFormProps {
  data: TokenAssociationData;
  onChange: (data: TokenAssociationData) => void;
}

const TokenAssociationForm = ({ data, onChange }: TokenAssociationFormProps) => {
  const { currentAccount, connectedNetwork } = useContext(GlobalContext);
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupError, setLookupError] = useState('');

  const handleChange = (field: keyof TokenAssociationData, value: string) => {
    onChange({ ...data, [field]: value });
  };

  const isTokenIdInvalid = data.tokenId !== '' && !isValidAddress(data.tokenId);

  const isAlreadyAssociated = data.tokenId !== '' &&
    currentAccount.tokens?.some(t => t.token_id === data.tokenId);

  const lookupToken = useCallback(async (tokenId: string) => {
    if (!tokenId || !isValidAddress(tokenId) || !connectedNetwork) return;

    setIsLookingUp(true);
    setLookupError('');

    try {
      const baseUrl = getMirrorBaseUrl(connectedNetwork);
      const isCustom = connectedNetwork?.startsWith('custom-');
      let tokenInfo: any;

      if (isCustom) {
        const client = authAxios();
        if (!client) throw new Error('Not authenticated');
        const res = await client.get(`${baseUrl}/api/v1/tokens/${tokenId}`);
        tokenInfo = res.data;
      } else {
        const response = await fetch(`${baseUrl}/api/v1/tokens/${tokenId}`);
        if (!response.ok) {
          setLookupError('Token not found on the network');
          onChange({ ...data, tokenName: '', tokenSymbol: '' });
          return;
        }
        tokenInfo = await response.json();
      }
      onChange({
        ...data,
        tokenId,
        tokenName: tokenInfo.name || '',
        tokenSymbol: tokenInfo.symbol || '',
      });
    } catch {
      setLookupError('Failed to fetch token info');
      onChange({ ...data, tokenName: '', tokenSymbol: '' });
    } finally {
      setIsLookingUp(false);
    }
  }, [connectedNetwork, data, onChange]);

  useEffect(() => {
    if (data.tokenId && isValidAddress(data.tokenId) && !data.tokenName) {
      lookupToken(data.tokenId);
    }
  }, [data.tokenId]);

  return (
    <Flex direction="column" gap={4} w="full">
      <Box>
        <Text mb={2} fontWeight={500}>Token ID</Text>
        <Input
          h={12}
          borderRadius={16}
          placeholder="0.0.xxxxx"
          value={data.tokenId}
          isInvalid={isTokenIdInvalid || !!isAlreadyAssociated}
          onChange={(e) => handleChange('tokenId', e.target.value)}
          onBlur={() => {
            if (data.tokenId && isValidAddress(data.tokenId) && !isAlreadyAssociated) {
              lookupToken(data.tokenId);
            }
          }}
        />
        {isTokenIdInvalid && (
          <Text color="red.500" size="xs" mt={1}>
            Invalid token ID format
          </Text>
        )}
        {isAlreadyAssociated && (
          <Text color="red.500" size="xs" mt={1}>
            This token is already associated with the account
          </Text>
        )}
        {lookupError && (
          <Text color="red.500" size="xs" mt={1}>
            {lookupError}
          </Text>
        )}
      </Box>

      {isLookingUp && (
        <Flex align="center" gap={2}>
          <Spinner size="sm" />
          <Text size="xs" color="blackAlpha.600">Looking up token...</Text>
        </Flex>
      )}

      {data.tokenName && (
        <Flex gap={4}>
          <Box flex={1}>
            <Text mb={2} fontWeight={500} color="blackAlpha.600">Token Name</Text>
            <Text size="sm">{data.tokenName}</Text>
          </Box>
          <Box flex={1}>
            <Text mb={2} fontWeight={500} color="blackAlpha.600">Symbol</Text>
            <Text size="sm">{data.tokenSymbol}</Text>
          </Box>
        </Flex>
      )}

      <Box>
        <Text mb={2} fontWeight={500}>Description (Optional)</Text>
        <Textarea
          borderRadius={16}
          placeholder="Why are you associating this token?"
          value={data.description}
          onChange={(e) => handleChange('description', e.target.value)}
          rows={2}
        />
      </Box>
    </Flex>
  );
};

export default TokenAssociationForm;
